import * as React from "react";
import { Eye, ImageOff, ImagePlus, X } from "lucide-react";

import { cn } from "../lib/utils";

/**
 * ImageGalleryGrid-Familie (TES-1999/TES-2000) — grosse, responsive Bild-Galerie
 * statt 48px-Thumbnails: Kacheln sind `aspect-square` und nutzen die verfuegbare
 * Breite; die Spaltenzahl passt sich der Kachel-Anzahl an, damit wenige Bilder
 * GROSS rendern („screen real estate perfectly").
 *
 * REINE UI-HUELLEN — der Aufrufer injiziert das eigentliche Bild (`image`-Slot,
 * z. B. eine auth-gefetchte Image-Komponente) und fertige i18n-Labels. Lightbox,
 * URL-Aufloesung und Datenanbindung bleiben in der App (kein zweiter Bildpfad).
 *
 * - `ImageGalleryGrid`       responsives Grid (Spalten adaptiv zur Kachel-Anzahl)
 * - `GalleryImageTile`       Bild-Kachel mit Hover-View-Overlay + optional Remove
 * - `GalleryPlaceholderTile` Kachel fuer kaputte/fehlende Bilder
 * - `GalleryAddTile`         gestrichelte „Foto hinzufuegen"-Kachel
 */

export interface ImageGalleryGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Maximale Spaltenzahl auf grossen Screens (Default 4). */
  columns?: 2 | 3 | 4;
}

/** Spalten adaptiv: wenige Kacheln → breite Kacheln; nie mehr Spalten als Kacheln.
 *  Auf sehr breiten Screens (xl) deckeln Einzelbilder bei 1/3-Breite, damit ein
 *  einzelnes Foto den Kontext nicht dominiert. */
const PAIR_COLS = "grid-cols-2 xl:grid-cols-3";
const CAPPED_COLS: Record<2 | 3 | 4, string> = {
  2: PAIR_COLS,
  3: "grid-cols-2 sm:grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-3 xl:grid-cols-4",
};

function galleryColsClass(count: number, columns: 2 | 3 | 4): string {
  if (count <= 1) return "grid-cols-1 sm:grid-cols-2 xl:grid-cols-3";
  if (count === 2) return PAIR_COLS;
  return CAPPED_COLS[columns];
}

const ImageGalleryGrid = React.forwardRef<HTMLDivElement, ImageGalleryGridProps>(
  ({ className, columns = 4, children, ...props }, ref) => {
    // toArray statt count: strippt null/false (z. B. konditionale Add-Kachel).
    const count = React.Children.toArray(children).length;
    return (
      <div
        ref={ref}
        className={cn("grid gap-4", galleryColsClass(count, columns), className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);
ImageGalleryGrid.displayName = "ImageGalleryGrid";

const TILE_BASE =
  "group relative aspect-square overflow-hidden rounded border border-outline-variant bg-surface-container";

export interface GalleryImageTileProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Bild-Slot — Aufrufer injiziert z. B. eine auth-gefetchte Image-Komponente. */
  image: React.ReactNode;
  /** Klick auf das View-Overlay (Lightbox des Aufrufers). Ohne Callback kein Overlay. */
  onView?: () => void;
  /** Klick auf den Remove-Button. Ohne Callback kein Button. */
  onRemove?: () => void;
  /** aria-label des View-Overlays (fertiger i18n-String). */
  viewLabel?: string;
  /** aria-label des Remove-Buttons (fertiger i18n-String). */
  removeLabel?: string;
  viewTestId?: string;
  removeTestId?: string;
}

const GalleryImageTile = React.forwardRef<HTMLDivElement, GalleryImageTileProps>(
  (
    {
      className,
      image,
      onView,
      onRemove,
      viewLabel,
      removeLabel,
      viewTestId,
      removeTestId,
      ...props
    },
    ref,
  ) => (
    <div ref={ref} className={cn(TILE_BASE, className)} {...props}>
      {image}
      {onView && (
        <button
          type="button"
          onClick={onView}
          aria-label={viewLabel}
          data-testid={viewTestId}
          className="absolute inset-0 flex items-center justify-center bg-primary/40 opacity-0 transition-opacity focus:opacity-100 focus:outline-none group-hover:opacity-100"
        >
          <Eye className="h-7 w-7 scale-125 text-white" aria-hidden="true" />
        </button>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={removeLabel}
          data-testid={removeTestId}
          className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-background/90 text-destructive opacity-0 transition-opacity hover:bg-background focus:opacity-100 focus:outline-none group-hover:opacity-100"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  ),
);
GalleryImageTile.displayName = "GalleryImageTile";

export type GalleryPlaceholderTileProps = React.HTMLAttributes<HTMLDivElement>;

const GalleryPlaceholderTile = React.forwardRef<HTMLDivElement, GalleryPlaceholderTileProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "relative flex aspect-square items-center justify-center overflow-hidden rounded border border-outline-variant bg-surface-container",
        className,
      )}
      {...props}
    >
      <ImageOff className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
    </div>
  ),
);
GalleryPlaceholderTile.displayName = "GalleryPlaceholderTile";

export interface GalleryAddTileProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Beschriftung der Add-Kachel (fertiger i18n-String). */
  label: string;
}

const GalleryAddTile = React.forwardRef<HTMLButtonElement, GalleryAddTileProps>(
  ({ className, label, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      className={cn(
        "flex aspect-square cursor-pointer flex-col items-center justify-center gap-2 rounded border-2 border-dashed border-outline-variant text-outline transition-colors hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <ImagePlus className="h-7 w-7 text-primary" aria-hidden="true" />
      <span className="text-label-caps text-muted-foreground">{label}</span>
    </button>
  ),
);
GalleryAddTile.displayName = "GalleryAddTile";

export { ImageGalleryGrid, GalleryImageTile, GalleryPlaceholderTile, GalleryAddTile };
